import _ from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';
import styled from 'styled-components';

import createEnum from 'scripts/lib/create_enum';
import InlineContainer from 'components/common/containers/inline_container';
import ReportDuration from 'components/reporting/report/lib/report_duration';
import SingleBarChart from '../lib/single_bar_chart';
import { TR, TD } from 'components/common/table';
import UnknownCustomerAvatar from 'components/common/icons/unknown_customer_avatar';

const MetricType = createEnum('COUNT', 'DURATION', 'PERCENT');

export function AgentPerformanceTableRow({ agent, agentPerformanceReportForAgent }) {
  if (!agent) {
    return null;
  }
  return (
    <AgentPerformanceRow>
      <WrappedCell>
        <AgentNameWrapper>
          <UnknownCustomerAvatar />
          {renderAgentName(agent.name)}
        </AgentNameWrapper>
      </WrappedCell>
      <MetricCell>{renderCustomersHelpedPerHour()}</MetricCell>
      <MetricCell>{renderPercentTimeHelpingCustomers()}</MetricCell>
      <MetricCell>{renderAvgActiveConversationHandleTime()}</MetricCell>
      <MetricCell>{renderPercentUtilized()}</MetricCell>
      <MetricCell>
        <LastMetric>{renderResponsesPerHour()}</LastMetric>
      </MetricCell>
    </AgentPerformanceRow>
  );

  function renderAgentName(name) {
    return (
      <div>
        <AgentName>{name || 'Agent'}</AgentName>
      </div>
    );
  }

  function renderCustomersHelpedPerHour() {
    return calculateMetric(
      'customersHelpedPerHour',
      'handleTimeByAgentIDAndDay.count',
      'utilizedTimeByAgentIDAndDay.duration',
      MetricType.COUNT
    );
  }

  function renderPercentTimeHelpingCustomers() {
    return calculateMetric(
      'percentTimeHelpingCustomers',
      'handleTimeByAgentIDAndDay.duration',
      'utilizedTimeByAgentIDAndDay.duration',
      MetricType.PERCENT
    );
  }

  function renderAvgActiveConversationHandleTime() {
    return calculateMetric(
      'conversationHandleTime',
      'averageHandleTimeByAgentIDAndDay.duration',
      'averageHandleTimeByAgentIDAndDay.count',
      MetricType.DURATION
    );
  }

  function renderPercentUtilized() {
    return calculateMetric(
      'percentUtilized',
      'utilizedTimeByAgentIDAndDay.duration',
      'loginTimeByAgentIDAndDay.duration',
      MetricType.PERCENT
    );
  }

  function renderResponsesPerHour() {
    return calculateMetric(
      'responsesPerHour',
      'responsesByAgentIDAndDay.count',
      'utilizedTimeByAgentIDAndDay.duration',
      MetricType.COUNT
    );
  }

  function calculateMetric(dataAid, numeratorMetric, denominatorMetric, metricType) {
    let numerator = _.get(agentPerformanceReportForAgent, numeratorMetric, null);
    let denominator = _.get(agentPerformanceReportForAgent, denominatorMetric, null);
    let barChart;
    const defaultValue = ' - ';
    let getResult = () => defaultValue;

    if (!denominator && metricType === MetricType.PERCENT) {
      getResult = () => <Percentage>{defaultValue}</Percentage>;
      barChart = () => <SingleBarChart isInvalid percentage={0} />;
    } else if (denominator) {
      const value = numerator / denominator;

      switch (metricType) {
        case MetricType.COUNT:
          getResult = () => _.round(value * 60 * 60 * 1000, 1).toFixed(1); // multiply b/c ms -> hr
          break;
        case MetricType.DURATION:
          getResult = () => <ReportDuration duration={value} />;
          break;
        case MetricType.PERCENT: {
          const roundedValue = _.round(value * 100, 1);
          getResult = () => <Percentage>{`${roundedValue.toFixed(1)}%`}</Percentage>;
          barChart = () => <SingleBarChart percentage={roundedValue} />;
          break;
        }
        default:
          getResult = () => defaultValue;
          break;
      }
    }

    return (
      <div data-aid={dataAid}>
        {!barChart ? null : barChart()}
        {getResult()}
      </div>
    );
  }
}

AgentPerformanceTableRow.propTypes = {
  agent: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string,
  }),
  agentPerformanceReportForAgent: PropTypes.object,
};

const AgentNameWrapper = styled(InlineContainer).attrs({ inset: 'none', inline: 'small' })`
  flex-direction: row;
  align-items: center;
  padding-left: 50px;
`;

export const AgentName = styled.div`
  font-weight: bold;
`;

const Percentage = styled.div`
  display: block;
  float: right;
  width: 45px;
`;

const WrappedCell = styled(TD)`
  word-break: break-word;
`;

const MetricCell = styled(TD)`
  text-align: right;
  word-break: break-word;
`;

export const LastMetric = styled.div`
  padding-right: 50px;
`;

const AgentPerformanceRow = styled(TR)`
  color: black;
`;

export default AgentPerformanceTableRow;
